"use client";

import React from "react";
import { Briefcase, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Link from "next/link";
import { useDealHooks } from "@/features/deals/hooks/use-deals-hooks";
import { LinkedEntityContextShell } from "./LinkedEntityContextShell";
import {
  getLinkedEntityFullPageHref,
  getTaskLinkedEntityId,
  normalizeTaskLinkedEntityType,
} from "@/lib/utils/crm/task-linked-entity";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Deal } from "@/lib/types/database/deals";
import { formatCurrency } from "@west-tennessee-consulting/ui/lib/currency";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

/**
 * Linked deal — task.entity_type `deals` + entity_id
 */
export function TaskLinkedDealPanel({ item }: TabContentProps<ContactTaskRow>) {
  const dealId = getTaskLinkedEntityId(item);
  const dealHooks = useDealHooks();
  const { data: deal, isLoading, error } = dealHooks.useSingle(dealId ?? "", {
    enabled: normalizeTaskLinkedEntityType(item.entity_type) === "deals" && !!dealId,
  });

  if (normalizeTaskLinkedEntityType(item.entity_type) !== "deals" || !dealId) {
    return null;
  }

  return (
    <LinkedEntityContextShell task={item} expectedKind="deals">
      <DealPanelBody dealId={dealId} deal={deal} isLoading={isLoading} error={error} />
    </LinkedEntityContextShell>
  );
}

function DealPanelBody({
  dealId,
  deal,
  isLoading,
  error,
}: {
  dealId: string;
  deal?: Deal;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Loading deal…</p>
        </CardContent>
      </Card>
    );
  }
  if (error || !deal) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-600 dark:text-red-400">
          <Briefcase className="h-12 w-12 mx-auto mb-4 text-red-200" />
          <p>Could not load this deal.</p>
        </CardContent>
      </Card>
    );
  }

  const href = getLinkedEntityFullPageHref("deals", dealId);
  const revenueText = formatCurrency(Number(deal.revenue) || 0, { currency: deal.currency || "USD", fallback: "", minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-card-foreground">Deal</h3>
        </div>
        <Button asChild variant="outline" size="sm" className="gap-2">
          <Link href={href}>
            <ExternalLink className="w-4 h-4" />
            Open deal
          </Link>
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{deal.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Stage: </span>
            <span className="capitalize text-card-foreground">{String(deal.stage).replace(/_/g, " ")}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Revenue: </span>
            <span className="font-medium text-card-foreground">{revenueText}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Probability: </span>
            <span>{deal.close_probability}%</span>
          </div>
          {deal.company_name ? (
            <div>
              <span className="text-gray-600 dark:text-gray-400">Company: </span>
              <span>{deal.company_name}</span>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
